import React from 'react';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { 
  ArrowRight,
  Globe,
  Info
} from 'lucide-react';
import { i18n } from '@/utils/i18n';
import { isFramedWindow } from '@vaiu/utils';

interface PaymentMethodPayPalProps {
  amount: number;
  currency: string;
  onSelect: () => void;
  onPay: () => void;
  disabled?: boolean;
  className?: string;
  isSelected?: boolean;
  /** Fallback when the embed cannot open a tab: real target=_top link. */
  approvalHref?: string;
}

export const PaymentMethodPayPal: React.FC<PaymentMethodPayPalProps> = ({
  amount,
  currency,
  onSelect,
  onPay,
  disabled = false,
  className,
  isSelected = false,
  approvalHref,
}) => {
  const framed = isFramedWindow();
  const embedCopy = approvalHref
    ? 'Popups are blocked. Continue with PayPal in this window (this site will leave the host page).'
    : 'PayPal opens in a new tab. Finish there; this page stays on the host site and updates when funding completes.';

  if (isSelected) {
    return (
      <div className={`space-y-4 rounded-md border border-muted-foreground/20 bg-muted/30 p-4 ${className ?? ''}`.trim()}>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              PayPal checkout
            </p>
            <p className="text-sm text-muted-foreground">
              {framed ? embedCopy : i18n.__('vaiu.topup.success.paypal_description')}
            </p>
          </div>
        </div>
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            {framed ? embedCopy : i18n.__('vaiu.topup.success.paypal_description')}
          </AlertDescription>
        </Alert>
        {approvalHref ? (
          <Button asChild className="w-full" disabled={disabled}>
            <a href={approvalHref} target="_top" rel="noopener">
              <ArrowRight className="w-4 h-4 mr-2" />
              Continue with PayPal
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            variant="default"
            onClick={onPay}
            disabled={disabled}
            className="w-full"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Continue with PayPal
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-3 rounded-md border border-muted-foreground/20 bg-muted/20 p-4 ${className ?? ''}`.trim()}>
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            PayPal checkout
          </p>
          <p className="text-sm text-muted-foreground">
            {i18n.__('vaiu.topup.success.paypal_description')}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onSelect}
        disabled={disabled}
        className="w-full"
      >
        <ArrowRight className="w-4 h-4 mr-2" />
        {i18n.__('vaiu.topup.tabs.paypal') || 'Select PayPal'}
      </Button>
    </div>
  );
};
